import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Shield, Loader2, CheckCircle, AlertTriangle, Zap } from "lucide-react";
import BrandLogo from "../components/BrandLogo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminInviteAccept() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [invite, setInvite] = useState(null);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    axios.get(`${API}/auth/invite/${token}`)
      .then(res => setInvite(res.data))
      .catch(err => setError(err.response?.data?.detail || "Invitation link not valid"))
      .finally(() => setLoading(false));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 10) { setError("Password must be at least 10 characters"); return; }
    if (password !== confirm) { setError("Passwords don't match"); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/auth/invite/accept`, { token, password });
      if (res.data?.session_token) localStorage.setItem("lf_session", res.data.session_token);
      navigate("/admin/dashboard");
    } catch (err) {
      setError(err.response?.data?.detail || "Could not accept invite");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-zinc-50 flex items-center justify-center px-4" data-testid="admin-invite-accept">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center justify-center gap-2 mb-6 font-extrabold text-zinc-950">
          <BrandLogo variant="mark" size={32} />
          FastPrototype.ai
        </Link>
        <div className="bg-white border border-zinc-200 rounded-3xl p-8">
          <div className="flex items-center gap-2 mb-3">
            <Shield size={18} className="text-zinc-700" />
            <h1 className="font-bold text-zinc-950 text-lg">Accept Admin Invitation</h1>
          </div>
          {loading ? (
            <div className="py-6 text-center"><Loader2 size={20} className="animate-spin text-zinc-400 mx-auto" /></div>
          ) : !invite ? (
            <div className="text-center py-6">
              <AlertTriangle size={28} className="text-red-500 mx-auto mb-3" />
              <p className="text-sm text-zinc-700">{error}</p>
              <Link to="/admin/login" className="mt-4 inline-block text-sm text-blue-600 underline">Back to login</Link>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-3" data-testid="invite-accept-form">
              <div className="bg-zinc-50 rounded-xl p-3 text-sm">
                <div className="text-xs text-zinc-500">Invited as</div>
                <div className="font-semibold text-zinc-900">{invite.name}</div>
                <div className="text-xs text-zinc-500" data-testid="invite-email">{invite.email}</div>
                {invite.invited_by_name && <div className="text-xs text-zinc-400 mt-1">by {invite.invited_by_name}</div>}
              </div>
              <div>
                <label className="text-xs font-semibold text-zinc-600">Set password (min 10 chars)</label>
                <input required type="password" data-testid="input-invite-password" value={password} onChange={e => setPassword(e.target.value)}
                  className="mt-1 w-full px-3 py-2.5 bg-zinc-50 border border-zinc-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900" />
              </div>
              <div>
                <label className="text-xs font-semibold text-zinc-600">Confirm password</label>
                <input required type="password" data-testid="input-invite-confirm" value={confirm} onChange={e => setConfirm(e.target.value)}
                  className="mt-1 w-full px-3 py-2.5 bg-zinc-50 border border-zinc-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-zinc-900" />
              </div>
              {error && <div data-testid="invite-error" className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>}
              <button type="submit" disabled={submitting} data-testid="btn-accept-invite"
                className="w-full py-3 bg-zinc-950 text-white rounded-full font-semibold text-sm hover:bg-zinc-800 disabled:opacity-50 inline-flex items-center justify-center gap-2">
                {submitting ? <><Loader2 size={14} className="animate-spin" /> Creating account…</> : <><CheckCircle size={14} /> Accept & create account</>}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
